// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Slingshot (PH-A21).
//
// The half of this that matters is already free: the kicking face is a **one-sided**
// segment, so the existing collider ignores contact from behind entirely. That is exactly
// the real mechanism — the switch blades sit behind the rubber, and nothing touching the
// back of the assembly can close them. PH-A21 therefore tests a property of the geometry
// rather than a special case in the device.
//
// What the device adds is the coil: a kick along the face normal, with a cooldown, capped
// so the slingshot cannot add energy to a ball already leaving faster than the arm moves.

import type { Ball } from '../Ball'
import {Plate,type PlateProfile} from '../Plate'
import type { Collider } from '../colliders'
import { makeSegment } from '../colliders'
import type { MaterialName } from '../materials'
import type { World } from '../World'
import { Device, type DeviceState } from './Device'
import { EV_SLINGSHOT_FIRED, EV_SWITCH_CLOSED } from '../events'

export interface SlingshotConfig {
  readonly cover?:readonly PlateProfile[]
  readonly id: string
  readonly a: readonly [number, number]
  readonly b: readonly [number, number]
  /**
   * Unit face normal, pointing into the play area, validated as perpendicular to `b − a`.
   *
   * **Omit it and order the points instead.** The default is the left-hand perpendicular of
   * `a → b`, so walking the face anticlockwise around the body puts the normal on the play
   * side automatically. Hand-typing a normal is a reliable way to get it slightly wrong:
   * `[0.714, 0.700]` for the standard left slingshot is out by 1.3 × 10⁻³ and is rejected —
   * the exact value is `[0.7150, 0.6991]`.
   */
  readonly normal?: readonly [number, number]
  /** Speed the ball leaves at, m/s. Real slingshots land in 3.5–5.0. */
  readonly kickSpeed?: number
  readonly cooldownSteps?: number
  /** Normal approach speed below which the rubber flexes but the blades do not close. */
  readonly triggerSpeed?: number
  readonly material?: MaterialName
}

export class Slingshot extends Device {
  readonly kind = 'slingshot'
  override readonly wantsContact:boolean
  private readonly cover:readonly Plate[]
  private readonly hardwareRadius:number
  override resolveContact(world:World,ball:Ball):boolean{
    const r=this.hardwareRadius+ball.radius
    if(ball.layer!==0||Math.min(ball.x,ball.prevX)-world.frameX>this.originX+r||Math.max(ball.x,ball.prevX)-world.frameX<this.originX-r||Math.min(ball.y,ball.prevY)-world.frameY>this.originY+r||Math.max(ball.y,ball.prevY)-world.frameY<this.originY-r)return false
    let hit=false
    const dx=this.bx-this.ax,dy=this.by-this.ay,length=Math.sqrt(dx*dx+dy*dy),nx=this.normal?.[0]??-dy/length,ny=this.normal?.[1]??dx/length
    for(const p of this.cover)if(p.resolve(world,ball)){
      hit=true
      if(p.active&&Math.abs(p.normalZ)<.1&&p.normalX*nx+p.normalY*ny>.1)this.onColliderHit(world,ball,p.normalX,p.normalY,p.impactSpeed)
    }
    return hit
  }

  private readonly ax: number
  private readonly ay: number
  private readonly bx: number
  private readonly by: number
  private readonly normal: readonly [number, number] | undefined
  private readonly kickSpeed: number
  private readonly cooldownSteps: number
  private readonly triggerSpeed: number
  private readonly material: MaterialName
  private readyAtStep = 0

  /** Kicks delivered since the last reset. */
  hits = 0

  /**
   * Step index of the most recent kick, or −1 if the coil has not fired since the last reset.
   *
   * A coil fire is instantaneous to the simulation and takes about forty milliseconds in the
   * world — the ring slams down, the arm punches out, and both come back. A renderer needs to
   * know *when*, and `hits` only says *how many*: recovering the moment from a counter means
   * the renderer keeping its own memory of the last value it saw, which is wrong after a
   * dropped frame and wrong again after a reset, and wrong in the quietest possible way — the
   * mechanism simply stops moving while the game plays on perfectly. One integer, written
   * where the counter is already written, removes the whole problem.
   */
  firedAtStep = -1

  protected override ownState(): DeviceState {
    return { readyAtStep: this.readyAtStep, hits: this.hits, firedAtStep: this.firedAtStep }
  }

  protected override restoreOwn(state: DeviceState): void {
    this.readyAtStep = Number(state['readyAtStep'])
    this.hits = Number(state['hits'])
    this.firedAtStep = Number(state['firedAtStep'])
  }

  constructor(config: SlingshotConfig) {
    super(config.id)
    this.cover=(config.cover??[]).map(p=>new Plate(p,config.a[0],config.a[1],config.b[0],config.b[1]));this.wantsContact=this.cover.length>0
    const cdx=config.b[0]-config.a[0],cdy=config.b[1]-config.a[1],length=Math.sqrt(cdx*cdx+cdy*cdy);this.hardwareRadius=0
    for(const p of config.cover??[])for(const q of p.outline)this.hardwareRadius=Math.max(this.hardwareRadius,Math.sqrt(q[0]*q[0]+q[1]*q[1])*length/p.referenceLength)
    this.ax = config.a[0]
    this.ay = config.a[1]
    this.bx = config.b[0]
    this.by = config.b[1]
    this.setOrigin((this.ax + this.bx) / 2, (this.ay + this.by) / 2)
    this.normal = config.normal
    this.kickSpeed = config.kickSpeed ?? 4.2
    this.cooldownSteps = config.cooldownSteps ?? 90
    this.triggerSpeed = config.triggerSpeed ?? 0.35
    this.material = config.material ?? 'rubber'
  }

  override colliders(): readonly Collider[] {
    const options: {
      oneSided: boolean
      device: number
      normal?: readonly [number, number]
    } = { oneSided: true, device: this.index }
    if (this.normal) options.normal = this.normal
    return [makeSegment(this.ax, this.ay, this.bx, this.by, this.material, options)]
  }

  override onColliderHit(
    world: World,
    ball: Ball,
    nx: number,
    ny: number,
    impactSpeed: number,
  ): void {
    if (impactSpeed < this.triggerSpeed) return
    if (world.stepIndex < this.readyAtStep) return
    this.readyAtStep = world.stepIndex + this.cooldownSteps
    this.firedAtStep = world.stepIndex
    this.hits++

    // Along the contact normal rather than the face normal, so a ball catching the very end
    // of the rubber is thrown around the corner instead of straight out — which is where
    // the slingshot's characteristic unpredictability comes from.
    const outward = ball.vx * nx + ball.vy * ny
    if (outward < this.kickSpeed) {
      const delta = this.kickSpeed - outward
      ball.vx += nx * delta
      ball.vy += ny * delta
    }

    world.events.emit(EV_SLINGSHOT_FIRED, this.index, ball.id, world.stepIndex, this.kickSpeed)
    world.events.emit(EV_SWITCH_CLOSED, this.index, ball.id, world.stepIndex, impactSpeed)
  }

  override reset(): void {
    this.readyAtStep = 0
    this.firedAtStep = -1
    this.hits = 0
  }
}
